import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LinkOutlined,
  TableOutlined,
  ColumnWidthOutlined,
  UploadOutlined,
  CalendarOutlined,
  TagOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  MinusOutlined,
  CheckSquareOutlined,DeleteOutlined
} from '@ant-design/icons';
import {
  MentionNode,
  LayoutNode,
  UploadNode,
  LayoutColumnNode,
  DateNode,
  CodeBlockNode,
  PanelNode,
  DividerNode,
  CheckboxNode,
} from './CustomNode';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $insertNodes,
  $isRootNode,
  $createTextNode,
  $getNodeByKey,
} from 'lexical';
import { Trash2 } from 'lucide-react';
import { Button } from 'antd';

const BlockControlsPlugin = ({onUpload}) => {
  const [editor] = useLexicalComposerContext();
  const [slashPosition, setSlashPosition] = useState(null);

  const [plus, setPlus] = useState({
    visible: false,
    top: 0,
    left: 0,
  });

  const [menuVisible, setMenuVisible] = useState(false);

  const getBlockElement = (target, root) => {
    let el = target;

    while (el && el !== root) {
      if (el.parentElement === root) return el;
      el = el.parentElement;
    }

    return null;
  };

  useEffect(() => {
    return editor.registerRootListener((root) => {
      if (!root) return;

      const handleClick = (e) => {
        const block = getBlockElement(e.target, root);

        if (!block) {
          setPlus((p) => ({ ...p, visible: false }));
          return;
        }

        const text = block.innerText.replace(/\u200B/g, '').trim();

        // SHOW ONLY IF EMPTY
        if (text.length === 0) {
          const rect = block.getBoundingClientRect();

          setPlus({
            visible: true,
            top: rect.top + rect.height / 2,
            left: rect.left - 32,
          });

          setMenuVisible(false);
        } else {
          setPlus((p) => ({ ...p, visible: false }));
        }
      };

      root.addEventListener('click', handleClick);
      root.addEventListener('mouseup', handleClick);
      root.addEventListener('keyup', handleClick);

      return () => {
        root.removeEventListener('click', handleClick);
        root.removeEventListener('mouseup', handleClick);
        root.removeEventListener('keyup', handleClick);
      };
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          setPlus((p) => ({ ...p, visible: false }));
          return;
        }

        let node = selection.anchor.getNode();

        while (node.getParent() && !$isRootNode(node.getParent())) {
          node = node.getParent();
        }

        const element = editor.getElementByKey(node.getKey());

        if (!element) return;

        const text = element.innerText.replace(/\u200B/g, '').trim();

        if (text.length > 0) {
          setPlus((p) => ({ ...p, visible: false }));
        }
      });
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          setSlashPosition(null);
          return;
        }

        const anchorNode = selection.anchor.getNode();
        const text = anchorNode.getTextContent();

        const offset = selection.anchor.offset;

        // Detect "/" typed
        if (text[offset - 1] === '/') {
          const dom = editor.getElementByKey(anchorNode.getKey());

          if (!dom) return;

          const rect = dom.getBoundingClientRect();

          setSlashPosition({
            top: rect.top + 24,
            left: rect.left + 10,
          });

          setMenuVisible(true);
        }
      });
    });
  }, [editor]);

  const deleteCurrentBlock = () => {
  editor.update(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) return;

    let node = selection.anchor.getNode();

    // go to top-level block
    while (node.getParent() && !$isRootNode(node.getParent())) {
      node = node.getParent();
    }

    node.remove();
  });
};

  const insertLink = () => {
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) return;

      let text = selection.getTextContent();

      if (!text.startsWith('http')) {
        text = 'https://' + text;
      }

      editor.dispatchCommand(TOGGLE_LINK_COMMAND, text);
    });
  };

  const insertTable = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: 4,
      columns: 4,
    });
  };

   const insertUpload = () => {
     const input = document.createElement('input');
     input.type = 'file';
 
     input.onchange = async () => {
       const file = input.files[0];
       if (!file) return;
 
       let inlinePosition = null;
 
       editor.update(() => {
         const selection = $getSelection();
 
         if (!$isRangeSelection(selection)) return;
 
         const anchor = selection.anchor;
         const focus = selection.focus;
 
         inlinePosition = {
           block_id: anchor.key,
           start_offset: anchor.offset,
           end_offset: focus.offset,
         };
       });
 
       const res = await onUpload(file, inlinePosition);
 
       const fileUrl = res?.file_url;
       const isImage = file.type.startsWith('image/');
 
       editor.update(() => {
         const selection = $getSelection();
         if (!$isRangeSelection(selection)) return;
 
         const node = new UploadNode(file.name, fileUrl, isImage, res?.id);
 
         selection.insertNodes([node]);
       });
     };
 
     input.click();
   };

  const insertLayout = () => {
    editor.update(() => {
      const layout = new LayoutNode();

      const col1 = new LayoutColumnNode();
      const col2 = new LayoutColumnNode();

      col1.append($createParagraphNode());
      col2.append($createParagraphNode());

      layout.append(col1, col2);

      $insertNodes([layout]);
    });
  };

  const insertDate = () => {
    editor.update(() => {
      const node = new DateNode(new Date().toLocaleDateString());

      const paragraph = $createParagraphNode();

      paragraph.append(node);

      $insertNodes([paragraph]);
    });
  };

  const insertMention = () => {
    editor.update(() => {
      const node = new MentionNode('Arya');

      $insertNodes([node]);
    });
  };

  const insertCodeBlock = () => {
    editor.update(() => {
      const code = new CodeBlockNode();
      const paragraph = $createParagraphNode();
      code.append(paragraph);
      $insertNodes([code]);
    });
  };

  const insertPanel = (type = 'info') => {
    editor.update(() => {
      const panel = new PanelNode(type);
      const paragraph = $createParagraphNode();
      panel.append(paragraph);
      $insertNodes([panel]);
    });
  };

  const insertDivider = () => {
    editor.update(() => {
      const divider = new DividerNode();
      $insertNodes([divider]);
    });
  };

  const insertCheckbox = () => {
    editor.update(() => {
      const checkbox = new CheckboxNode(false);
      const paragraph = $createParagraphNode();
      checkbox.append(paragraph);
      $insertNodes([checkbox]);
    });
  };

  const removeSlashCharacter = () => {
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) return;

      const anchor = selection.anchor;
      const node = anchor.getNode();

      if (node.getTextContent().endsWith('/')) {
        node.setTextContent(node.getTextContent().slice(0, -1));
      }
    });
  };

  // MENU ITEM UI
  const Item = ({ icon, title, desc, onClick }) => (
    <div
      onClick={() => {
        removeSlashCharacter();
        onClick();
        setMenuVisible(false);
        setSlashPosition(null);
      }}
      style={{
        display: 'flex',
        gap: 12,
        padding: '10px 14px',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f5f7')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
    >
      <div style={{ fontSize: 18 }}>{icon}</div>

      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#666' }}>{desc}</div>
      </div>
    </div>
  );

  // MENU
  const Menu = () =>
    createPortal(
      <div
        style={{
          position: 'fixed',
          top: slashPosition ? slashPosition.top : plus.top,
          left: slashPosition ? slashPosition.left : plus.left + 30,
          width: 280,
          maxHeight: 400,
          overflowY: 'auto',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          zIndex: 10000,
        }}
      >
        <Item
          icon={<LinkOutlined />}
          title="Link"
          desc="Insert hyperlink"
          onClick={insertLink}
        />

        <Item
          icon={<TableOutlined />}
          title="Table"
          desc="Insert table"
          onClick={insertTable}
        />

        <Item
          icon={<ColumnWidthOutlined />}
          title="Layout"
          desc="Multi column layout"
          onClick={insertLayout}
        />

        <Item
          icon={<UploadOutlined />}
          title="Upload"
          desc="Upload file/image"
          onClick={insertUpload}
        />

        <Item
          icon={<CalendarOutlined />}
          title="Date"
          desc="Insert date"
          onClick={insertDate}
        />

        <Item
          icon={<TagOutlined />}
          title="Mention"
          desc="Mention user"
          onClick={insertMention}
        />

        <Item
          icon={<CodeOutlined />}
          title="Code Block"
          desc="Insert code block"
          onClick={insertCodeBlock}
        />

        <Item
          icon={<InfoCircleOutlined />}
          title="Info Panel"
          desc="Info panel"
          onClick={() => insertPanel('info')}
        />

        <Item
          icon={<CheckCircleOutlined />}
          title="Success Panel"
          desc="Success panel"
          onClick={() => insertPanel('success')}
        />

        <Item
          icon={<WarningOutlined />}
          title="Warning Panel"
          desc="Warning panel"
          onClick={() => insertPanel('warning')}
        />

        <Item
          icon={<CloseCircleOutlined />}
          title="Error Panel"
          desc="Error panel"
          onClick={() => insertPanel('error')}
        />

        <Item
          icon={<MinusOutlined />}
          title="Divider"
          desc="Insert divider"
          onClick={insertDivider}
        />

        <Item
          icon={<CheckSquareOutlined />}
          title="Checkbox"
          desc="Insert checkbox"
          onClick={insertCheckbox}
        />
      </div>,
      document.body,
    );

  // PLUS BUTTON
  const PlusButton = () =>
    plus.visible &&
    createPortal(
      <div
        onClick={() => setMenuVisible(!menuVisible)}
        style={{
          position: 'fixed',
          top: plus.top - 12,
          left: plus.left,
          width: 24,
          height: 24,
          borderRadius: 6,
          border: '1px solid #ccc',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 10000,
          fontWeight: 'bold',
        }}
      >
        +
      </div>,
      document.body,
    );

  return (
    <>
      <PlusButton />
      {menuVisible && <Menu />}
     <Button icon={<DeleteOutlined />} onClick={deleteCurrentBlock} />
    </>
  );
};

export default BlockControlsPlugin;
